import React, { useState, useEffect, useRef } from "react";
import {
  Lightbulb, Search, Loader2, AlertCircle, Plus, BookOpen, Clock, ToggleLeft, ToggleRight, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { fetchCitationHints, fetchCitationSources } from "@/lib/openaiClient";

type Hint = {
  text: string;
  reason: string;
  suggestedSearchQuery?: string;
};

type Source = {
  title: string;
  authors: string;
  year: string;
  journal: string;
  url: string;
  type: string;
  summary?: string;
};

type CitationAdvisorPanelProps = {
  documentId?: number;
  documentContent: string;
  formatStyle: string;
  citations?: any[];
  onInsertCitation?: (citation: any) => void;
  onAddCitation?: (citation: any) => void;
  continuousMonitoring?: boolean;
  onToggleContinuousMonitoring?: (on: boolean) => void;
  onNavigateToText?: (text: string) => void;
};

const MOCK_HINTS: Hint[] = [
  {
    text: "The global population has surpassed 8 billion.",
    reason: "factual assertion",
    suggestedSearchQuery: "global population statistics"
  },
  {
    text: "Recent studies show a link between sleep and memory retention.",
    reason: "references a study",
    suggestedSearchQuery: "sleep and memory retention research"
  }
];

const MOCK_SOURCES: Source[] = [
  {
    title: "World Population Prospects 2022",
    authors: "United Nations",
    year: "2022",
    journal: "UN Report",
    summary: "United Nations demographic projections covering global population milestones.",
    url: "https://population.un.org/wpp/",
    type: "report"
  },
  {
    title: "Sleep Enhances Memory Consolidation",
    authors: "J. Smith, L. Wang",
    year: "2021",
    journal: "Nature Neuroscience",
    summary: "Peer-reviewed article detailing mechanisms that link REM sleep to memory retention.",
    url: "https://nature.com/articles/sleep-memory",
    type: "journal"
  }
];

const aiAssistAvailable = Boolean(
  (import.meta.env.VITE_SUPABASE_FUNCTIONS_URL ?? import.meta.env.VITE_SUPABASE_URL) &&
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const CitationAdvisorPanel: React.FC<CitationAdvisorPanelProps> = ({
  documentId,
  documentContent,
  formatStyle,
  citations,
  onInsertCitation,
  onAddCitation,
  continuousMonitoring = false,
  onToggleContinuousMonitoring,
  onNavigateToText
}) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [hints, setHints] = useState<Hint[]>([]);
  const [findingSourcesFor, setFindingSourcesFor] = useState<Hint | null>(null);
  const [sourceResults, setSourceResults] = useState<Source[]>([]);
  const [selectedSources, setSelectedSources] = useState<Source[]>([]);
  const [searchPrefs, setSearchPrefs] = useState({
    sourceType: "any",
    timeFrame: "5years"
  });
  const [loadingSources, setLoadingSources] = useState(false);
  const [extractingRefs, setExtractingRefs] = useState(false);
  const [extractedCount, setExtractedCount] = useState(0);

  const lastAnalyzedContent = useRef("");

  // Simulate continuous monitoring (local only)
  useEffect(() => {
    if (!continuousMonitoring || !documentContent) return;
    const contentDiff = Math.abs(documentContent.length - lastAnalyzedContent.current.length);
    if (contentDiff < 100) return;
    const timer = setTimeout(() => {
      analyzeDocument();
      lastAnalyzedContent.current = documentContent;
    }, 5000);
    return () => clearTimeout(timer);
  }, [documentContent, continuousMonitoring]);

  // Simulate extracting references from document
  const extractReferencesFromDocument = async () => {
    if (!documentId || !documentContent || extractingRefs) return;
    const plainText = documentContent.replace(/<[^>]*>/g, '');
    const lowerContent = plainText.toLowerCase();
    const refIndex = Math.max(
      lowerContent.lastIndexOf('references'),
      lowerContent.lastIndexOf('works cited'),
      lowerContent.lastIndexOf('bibliography')
    );
    if (refIndex === -1) {
      setExtractedCount(0);
      setExtractingRefs(false);
      return;
    }
    setExtractingRefs(true);
    setTimeout(() => {
      setExtractedCount(3);
      setExtractingRefs(false);
    }, 1200);
  };

  // Simulate analyzing document for citation hints
  const analyzeDocument = async () => {
    if (!documentContent) return;
    setAnalyzing(true);
    try {
      const plain = documentContent.replace(/<[^>]*>/g, "");
      if (aiAssistAvailable) {
        const aiHints = await fetchCitationHints({ content: plain, maxHints: 6 });
        setHints(aiHints.length ? aiHints : MOCK_HINTS);
      } else {
        setHints(MOCK_HINTS);
      }
    } catch (err) {
      console.error("Citation hint analysis failed", err);
      setHints(MOCK_HINTS);
    } finally {
      setAnalyzing(false);
    }
  };

  // Simulate finding sources for a hint
  const findSources = async (hint: Hint) => {
    setLoadingSources(true);
    try {
      if (aiAssistAvailable) {
        const aiSources = await fetchCitationSources({
          hint: hint.text,
          sourceType: searchPrefs.sourceType,
          timeFrame: searchPrefs.timeFrame,
        });
        setSourceResults(aiSources.length ? aiSources : MOCK_SOURCES);
      } else {
        setSourceResults(MOCK_SOURCES);
      }
      setSelectedSources([]);
    } catch (err) {
      console.error("Source search failed", err);
      setSourceResults(MOCK_SOURCES);
    } finally {
      setLoadingSources(false);
    }
  };

  const formatInTextCitation = (source: Source) => {
    const authorLast = source.authors?.split(',')[0]?.split(' ').pop() || "Author";
    const year = source.year || "n.d.";
    switch (formatStyle) {
      case "MLA":
        return `(${authorLast})`;
      case "Chicago":
        return `(${authorLast} ${year})`;
      case "Harvard":
        return `(${authorLast}, ${year})`;
      case "APA":
      default:
        return `(${authorLast}, ${year})`;
    }
  };

  const handleAddSelectedSources = async () => {
    for (const source of selectedSources) {
      const newCitation = {
        document_id: documentId,
        title: source.title,
        authors: [{ first_name: source.authors?.split(' ')[0] || "", last_name: source.authors?.split(' ').slice(1).join(' ') || source.authors }],
        year: source.year,
        source: source.journal,
        url: source.url,
        citation_type: source.type || "journal",
        in_text_key: `${source.authors?.split(',')[0]?.split(' ').pop() || "Author"}${source.year || ""}`
      };
      if (onAddCitation) {
        await onAddCitation(newCitation);
      }
      const inTextCitation = formatInTextCitation(source);
      if (onInsertCitation) {
        onInsertCitation(` ${inTextCitation}`);
      }
    }
    setFindingSourcesFor(null);
    setSourceResults([]);
    setSelectedSources([]);
  };

  const toggleSourceSelection = (source: Source) => {
    setSelectedSources(prev => {
      const exists = prev.find(s => s.title === source.title);
      if (exists) {
        return prev.filter(s => s.title !== source.title);
      }
      return [...prev, source];
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold text-white">Citation Advisor</h3>
        </div>
        <p className="text-xs text-slate-400 mb-3">
          AI-powered citation detection and source finding
        </p>
        <div className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/10 mb-3">
          <div className="flex items-center gap-2">
            {continuousMonitoring ? (
              <ToggleRight className="w-5 h-5 text-emerald-400" />
            ) : (
              <ToggleLeft className="w-5 h-5 text-slate-500" />
            )}
            <div>
              <p className="text-xs text-white">Auto-monitor</p>
              <p className="text-[10px] text-slate-500">Uses credits</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onToggleContinuousMonitoring?.(!continuousMonitoring)}
            className={`h-7 px-2 text-xs ${continuousMonitoring ? 'text-emerald-400 hover:text-emerald-300' : 'text-slate-400 hover:text-white'}`}
          >
            {continuousMonitoring ? 'ON' : 'OFF'}
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={analyzeDocument}
            disabled={analyzing || !documentContent}
            className="flex-1 btn-gradient text-white"
            size="sm"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Analyze
              </>
            )}
          </Button>
          <Button
            onClick={extractReferencesFromDocument}
            disabled={extractingRefs || !documentContent}
            variant="outline"
            className="flex-1 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
            size="sm"
          >
            {extractingRefs ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Extract Refs
              </>
            )}
          </Button>
        </div>
        {extractedCount > 0 && (
          <p className="text-xs text-emerald-400 mt-2 text-center">
            ✓ {extractedCount} references auto-added
          </p>
        )}
      </div>

      <ScrollArea className="flex-1 p-4">
        {hints.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">
              Click "Analyze Document" to find sentences that may need citations.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-400 mb-2">
              Found {hints.length} potential citation needs:
            </p>
            {hints.map((hint, index) => (
              <div
                key={index}
                className="p-3 rounded-lg bg-white/5 border border-white/10"
              >
                <p className="text-sm text-white mb-2 line-clamp-2">
                  "{hint.text}"
                </p>
                <Badge variant="outline" className="text-xs text-amber-400 border-amber-400/30 mb-2">
                  {hint.reason}
                </Badge>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-blue-300 hover:text-blue-200 hover:bg-blue-500/20"
                    onClick={() => onNavigateToText?.(hint.text)}
                  >
                    <Search className="w-3 h-3 mr-1" />
                    Go to Text
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-purple-300 hover:text-purple-200 hover:bg-purple-500/20"
                    onClick={() => {
                      setFindingSourcesFor(hint);
                      findSources(hint);
                    }}
                  >
                    <BookOpen className="w-3 h-3 mr-1" />
                    Find Sources
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs text-slate-400 hover:text-slate-300"
                    onClick={() => setHints(hints.filter((_, i) => i !== index))}
                  >
                    Ignore
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Find Sources Dialog */}
      <Dialog open={!!findingSourcesFor} onOpenChange={() => setFindingSourcesFor(null)}>
        <DialogContent className="max-w-lg bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Find Sources</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Select value={searchPrefs.sourceType} onValueChange={(v) => setSearchPrefs(p => ({ ...p, sourceType: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Source type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="any">Any Type</SelectItem>
                  <SelectItem value="article">Article</SelectItem>
                  <SelectItem value="book">Book</SelectItem>
                  <SelectItem value="journal">Journal</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                </SelectContent>
              </Select>
              <Select value={searchPrefs.timeFrame} onValueChange={(v) => setSearchPrefs(p => ({ ...p, timeFrame: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <Clock className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Time frame" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  <SelectItem value="any">Any Time</SelectItem>
                  <SelectItem value="5years">Last 5 Years</SelectItem>
                  <SelectItem value="10years">Last 10 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {findingSourcesFor && (
              <div className="p-2 rounded bg-white/5 text-xs text-slate-400">
                Searching for: "{findingSourcesFor.text?.substring(0, 100)}..."
              </div>
            )}
            {loadingSources ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
              </div>
            ) : (
              <ScrollArea className="max-h-64">
                <div className="space-y-2">
                  {sourceResults.map((source, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedSources.find(s => s.title === source.title)
                          ? "bg-purple-500/20 border-purple-500/50"
                          : "bg-white/5 border-white/10 hover:bg-white/10"
                      }`}
                      onClick={() => toggleSourceSelection(source)}
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={!!selectedSources.find(s => s.title === source.title)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="text-sm text-white font-medium">{source.title}</p>
                          <p className="text-xs text-slate-400">{source.authors} ({source.year})</p>
                          <p className="text-xs text-slate-500">{source.journal}</p>
                          {source.summary && (
                            <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{source.summary}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFindingSourcesFor(null)} className="text-slate-400">
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedSources}
              disabled={selectedSources.length === 0}
              className="btn-gradient text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {selectedSources.length} Source{selectedSources.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CitationAdvisorPanel;